import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { TextField } from '@yoast/ui-library';
import { useDeleteUserData } from '../api/generated/insights';
import { useToast } from '../hooks';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface DeleteAllDataModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DeleteAllDataModal: React.FC<DeleteAllDataModalProps> = ({
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [confirmationValue, setConfirmationValue] = useState('');

  const { mutate: deleteUserData, isPending } = useDeleteUserData({
    mutation: {
      onSettled: () => {
        // Optionally invalidate all queries or reset cache
        queryClient.clear();
      },
      onSuccess: () => {
        // After successful deletion:
        onClose();
        // show a success toast
        toast('Successfully deleted all data.', { variant: 'success' });
      },
      onError: () => {
        // Optionally invalidate all queries or reset cache
        queryClient.clear();
        toast('Failed to delete all data. Please try again later.', {
          variant: 'error',
        });
      },
    },
  });

  useEffect(() => {
    if (isOpen) {
      setConfirmationValue(''); // Reset confirmation input when modal opens
    }
  }, [isOpen]);

  return (
    <DeleteConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={() => {
        deleteUserData();
      }}
      isLoading={isPending}
      disabled={confirmationValue !== 'DELETE ALL'}
      title="Delete all data?"
      description={
        <>
          <p>
            This will permanently erase all brands, analysis results, and
            queries from your account.
          </p>
          <p>This action cannot be undone.</p>
          <TextField
            id="delete-all-confirmation"
            label="To confirm, please type DELETE ALL:"
            value={confirmationValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfirmationValue(e.target.value)
            }
          />
          <p>
            <em>
              This is to make sure you understand this action is permanent.
            </em>
          </p>
        </>
      }
      confirmText="Yes, delete all data"
      cancelText="No, go back"
    />
  );
};

export default DeleteAllDataModal;
